import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardService } from '@/services/dashboardService';
import { PageLoadingSpinner } from '@/components/ui/LoadingSpinner';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import { useTenant } from '@/contexts/TenantContext';

/**
 * DefaultDashboardRedirect
 *
 * Redirects users to the default dashboard or first available dashboard.
 * If no dashboards exist, shows a prompt to create one.
 */
export function DefaultDashboardRedirect() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [noDashboards, setNoDashboards] = useState(false);
  const { currentTenant, isLoading: tenantLoading } = useTenant();

  useEffect(() => {
    // Wait for tenant context to initialize
    if (tenantLoading || !currentTenant) {
      return;
    }

    /**
     * USR-1.5. `cancelled` is the whole fix, and the bug it closes was not a dashboard
     * bug at all.
     *
     * This effect awaits one or two network round trips and THEN navigates. Without a
     * guard the navigate fires even when the user has already gone somewhere else,
     * because an unmounted component's pending promise still runs its continuation. In
     * practice: sign in, land on `/app/dashboard`, click Settings within the second or
     * two the fetch takes, arrive at Settings, and then get yanked to
     * `/app/dashboards/<id>` by a callback belonging to a screen you already left.
     *
     * It reads as a permissions failure from the outside, which is exactly how it was
     * first misdiagnosed: the destination loads, the URL is briefly correct, and then
     * something throws you back to the dashboard. It also blinded the entire
     * user-management E2E suite, whose specs navigate immediately after logging in.
     *
     * ⚠️ The `replace: true` made it worse rather than better. The clobbering navigation
     * leaves no history entry, so pressing Back does not return to the page the user
     * actually asked for.
     */
    let cancelled = false;

    const redirectToDefaultDashboard = async () => {
      try {
        const defaultDashboard = await DashboardService.getDefaultDashboard();
        if (cancelled) return;

        if (defaultDashboard) {
          // Redirect to the default dashboard
          navigate(`/app/dashboards/${defaultDashboard.id}`, { replace: true });
        } else {
          // No default dashboard set - check if any dashboards exist
          const allDashboards = await DashboardService.getDashboards();
          if (cancelled) return;

          if (allDashboards.length > 0) {
            // Redirect to the first available dashboard
            navigate(`/app/dashboards/${allDashboards[0].id}`, { replace: true });
          } else {
            // Truly no dashboards exist
            setNoDashboards(true);
            setLoading(false);
          }
        }
      } catch (error) {
        if (cancelled) return;
        console.error('Failed to fetch default dashboard:', error);
        setNoDashboards(true);
        setLoading(false);
      }
    };

    redirectToDefaultDashboard();

    return () => {
      cancelled = true;
    };
  }, [navigate, tenantLoading, currentTenant]);

  if (loading || tenantLoading) {
    return <PageLoadingSpinner message="Loading dashboard..." />;
  }

  if (noDashboards) {
    return (
      <div className="flex flex-col items-center justify-center h-[calc(100vh-200px)] space-y-6">
        <div className="text-center space-y-2">
          <h2 className="text-2xl font-bold text-foreground">No Dashboards Found</h2>
          <p className="text-muted-foreground max-w-md">
            Get started by creating your first dashboard. You can add widgets to track
            projects, appointments, tasks, and more.
          </p>
        </div>
        <Button
          onClick={() => navigate('/app/dashboards/new')}
          size="lg"
          className="gap-2"
        >
          <Plus className="h-5 w-5" />
          Create Your First Dashboard
        </Button>
      </div>
    );
  }

  return null;
}
